import {
  GoogleAuthProvider,
  onAuthStateChanged as onFirebaseAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  reauthenticateWithPopup,
  type User,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { err, ok, type Result } from '@shared/result';
import { asId } from '@shared/brand';
import type {
  AuthenticatedUser,
  IAuthGateway,
  SessionClaims,
  SignInError,
} from '@application/ports';
import { belongsToAllowedDomain } from '@domain/value-objects/CorporateEmail';
import { isRole } from '@domain/vocabulary';
import { firebase } from './client';
import { clearCursorCache } from '../repositories/support';

/**
 * Google sign-in, and the client's part in the corporate-domain rule.
 *
 * The domain is enforced in three places, and it is worth being precise about
 * which of them is actually load-bearing:
 *
 *  1. **Here, via the `hd` parameter.** It filters Google's own account chooser so
 *     a personal address is not even offered. Pure convenience — a user can strip
 *     the parameter. **Not a security control.**
 *
 *  2. **The blocking function `beforeSignIn`.** Runs on Identity Platform before a
 *     token is ever minted. A non-corporate address is rejected there and never
 *     obtains a session. **This is the real gate.**
 *
 *  3. **The Firestore rules.** Re-check the token's email and `email_verified` on
 *     every operation, so a token from a misconfigured provider still reads nothing.
 *
 * The local check below exists only so that, if layer 2 somehow lets something
 * through, the user is signed straight back out and shown a clear message instead
 * of landing in an app where every read fails with a permission error.
 */

export class FirebaseAuthGateway implements IAuthGateway {
  async signInWithGoogle(domainHint: string): Promise<Result<AuthenticatedUser, SignInError>> {
    const { auth, env } = firebase();

    const provider = new GoogleAuthProvider();
    // Narrows Google's chooser to the corporate tenant. A hint, not a boundary.
    provider.setCustomParameters({ hd: domainHint, prompt: 'select_account' });

    try {
      const credential = await signInWithPopup(auth, provider);
      const user = credential.user;

      const allowed = [domainHint, env.corporateDomain].filter((d) => d.length > 0);
      if (user.email === null || !belongsToAllowedDomain(user.email, allowed)) {
        // Defence in depth. If the blocking function is not deployed yet, this is
        // what stops a personal account from reaching a broken-looking app.
        await firebaseSignOut(auth);
        return err({
          kind: 'wrong-domain',
          email: user.email ?? '(sin correo)',
          allowed,
        });
      }

      return ok(await this.toAuthenticatedUser(user));
    } catch (cause) {
      return err(translateSignInError(cause));
    }
  }

  /**
   * Password sign-in for the QA accounts.
   *
   * The domain check below is the same one Google sign-in gets, and for the same
   * reason: it is not the boundary — `beforeSignIn` is — but it turns a rejection
   * into a sentence the person can read instead of an app where every read fails.
   *
   * There is no sign-up path here on purpose. These accounts are created out of
   * band with the Admin SDK, which is also the only way they can be marked
   * verified; anyone signing themselves up at a corporate address gets an
   * unverified account that the blocking function refuses.
   */
  async signInWithPassword(
    email: string,
    password: string,
  ): Promise<Result<AuthenticatedUser, SignInError>> {
    const { auth, env } = firebase();

    try {
      const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
      const user = credential.user;

      const allowed = [env.corporateDomain].filter((d) => d.length > 0);
      if (user.email === null || !belongsToAllowedDomain(user.email, allowed)) {
        await firebaseSignOut(auth);
        return err({ kind: 'wrong-domain', email: user.email ?? '(sin correo)', allowed });
      }

      return ok(await this.toAuthenticatedUser(user));
    } catch (cause) {
      return err(translateSignInError(cause));
    }
  }

  async signOut(): Promise<void> {
    // Pagination cursors are cached per session; leaving them would let a second
    // sign-in on the same device resume the first user's paging.
    clearCursorCache();
    await firebaseSignOut(firebase().auth);
  }

  /**
   * Vuelve a pasar por Google sin cerrar la sesión.
   *
   * `reauthenticateWithPopup` y no `signInWithPopup`: el segundo cambiaría de
   * usuario si alguien elige otra cuenta en el selector, y aquí lo que se
   * comprueba es que **este** dueño sigue delante. Firebase se niega si la cuenta
   * elegida no es la de la sesión, que es exactamente la respuesta correcta.
   *
   * `prompt: 'select_account'` a propósito, aunque parezca contradictorio: sin
   * él, Google contesta al instante con la sesión que ya tiene abierta y el paso
   * se vuelve decorativo — un clic que no comprueba nada.
   */
  async reauthenticate(): Promise<boolean> {
    const { auth, env } = firebase();
    const user = auth.currentUser;
    if (user === null) return false;

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      hd: env.corporateDomain,
      prompt: 'select_account',
      ...(user.email === null ? {} : { login_hint: user.email }),
    });

    try {
      await reauthenticateWithPopup(user, provider);
      return true;
    } catch {
      /* Cerró la ventana, eligió otra cuenta o no hay red. Ninguna de las tres
         autoriza a enseñar nada, y ninguna es motivo para tirar la pantalla. */
      return false;
    }
  }

  currentUser(): AuthenticatedUser | null {
    const user = firebase().auth.currentUser;
    if (user === null) return null;
    // Synchronous accessor: returns the claims already attached to the cached
    // token rather than forcing a refresh. `refreshClaims` is the async path.
    return {
      uid: user.uid,
      email: user.email ?? '',
      displayName: user.displayName ?? '',
      photoURL: user.photoURL,
      emailVerified: user.emailVerified,
      claims: EMPTY_CLAIMS,
    };
  }

  /**
   * Subscribes to session changes.
   *
   * The listener resolves claims before notifying, because a consumer that saw the
   * user first and the claims a tick later would render the app once with no role —
   * a visible flash of an empty navigation on every load.
   */
  onAuthStateChanged(listener: (user: AuthenticatedUser | null) => void): () => void {
    return onFirebaseAuthStateChanged(firebase().auth, (user) => {
      if (user === null) {
        listener(null);
        return;
      }
      void this.toAuthenticatedUser(user).then(listener);
    });
  }

  /**
   * Force-refreshes the ID token to pick up newly minted claims.
   *
   * Called after an administrator validates an account: the Cloud Function sets the
   * custom claims, but the client's cached token predates them and would keep
   * reading as `pending` for up to an hour. This is what makes activation feel
   * immediate instead of requiring a sign-out.
   */
  async refreshClaims(): Promise<Result<AuthenticatedUser, SignInError>> {
    const user = firebase().auth.currentUser;
    if (user === null) return err({ kind: 'unknown', detail: 'No hay sesión activa.' });

    try {
      await user.getIdToken(true);
      return ok(await this.toAuthenticatedUser(user));
    } catch (cause) {
      return err(translateSignInError(cause));
    }
  }

  private async toAuthenticatedUser(user: User): Promise<AuthenticatedUser> {
    const token = await user.getIdTokenResult();
    return {
      uid: user.uid,
      email: (user.email ?? '').toLowerCase(),
      displayName: user.displayName ?? user.email ?? '',
      photoURL: user.photoURL,
      emailVerified: user.emailVerified,
      claims: readClaims(token.claims),
    };
  }
}

const EMPTY_CLAIMS: SessionClaims = {
  role: null,
  areaId: null,
  personId: null,
  status: null,
};

/**
 * Reads our custom claims off the token.
 *
 * Absent claims are the normal state for a freshly created account: the person
 * exists in `people` as `pending`, holds no role, and the security rules therefore
 * deny every read. That is the intended behaviour — a valid Google sign-in on the
 * corporate domain grants access to nothing until an administrator validates it.
 *
 * Anything unrecognised collapses to null rather than being coerced. Guessing a
 * role from a malformed claim is the one failure mode that must never happen.
 */
const readClaims = (raw: Readonly<Record<string, unknown>>): SessionClaims => {
  const role = raw['role'];
  const areaId = raw['areaId'];
  const personId = raw['personId'];
  const status = raw['status'];

  return {
    role: isRole(role) ? role : null,
    areaId:
      typeof areaId === 'string' && areaId.length > 0 ? (asId<'AreaId'>(areaId)) : null,
    personId:
      typeof personId === 'string' && personId.length > 0
        ? (asId<'PersonId'>(personId))
        : null,
    status:
      status === 'pending' || status === 'active' || status === 'inactive' ? status : null,
  };
};

/**
 * Maps Firebase's auth error codes onto the port's error type.
 *
 * `popup-closed-by-user` is not a failure worth showing an error for — the person
 * changed their mind — so it is distinguished from the cases that genuinely need a
 * message. The blocking function's rejection surfaces as
 * `auth/internal-error` with our own message inside it, which is why the domain
 * case is matched on the message text as well as the code.
 */
const translateSignInError = (cause: unknown): SignInError => {
  if (!(cause instanceof FirebaseError)) {
    return { kind: 'unknown', detail: cause instanceof Error ? cause.message : String(cause) };
  }

  switch (cause.code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return { kind: 'popup-closed' };
    case 'auth/popup-blocked':
      return { kind: 'popup-blocked' };
    case 'auth/user-disabled':
      return { kind: 'account-disabled' };
    /*
     * One answer for all three, and that is the point. Firebase distinguishes a
     * missing account from a wrong password, and repeating that distinction to
     * the browser turns the login form into a way to ask which corporate
     * addresses exist. `auth/invalid-credential` is what a project with email
     * enumeration protection already returns; the other two cover projects
     * where it is off.
     */
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return { kind: 'bad-credentials' };
    case 'auth/network-request-failed':
      return { kind: 'network' };
    default:
      // The blocking function rejects with a message we control; surfacing it as a
      // domain error gives the user the actionable sentence instead of "internal
      // error".
      if (/dominio|domain/i.test(cause.message)) {
        return { kind: 'wrong-domain', email: '', allowed: [] };
      }
      return { kind: 'unknown', detail: `${cause.code}: ${cause.message}` };
  }
};

/** User-facing copy for each sign-in failure, in the design's voice. */
export const signInErrorMessage = (error: SignInError): string => {
  switch (error.kind) {
    case 'popup-closed':
      return 'Cerraste la ventana de Google antes de terminar. Vuelve a intentarlo.';
    case 'popup-blocked':
      return 'El navegador bloqueó la ventana de Google. Permite las ventanas emergentes de este sitio.';
    case 'wrong-domain': {
      const domains = error.allowed.filter((d) => d.length > 0);
      const suffix = domains.length > 0 ? ` Solo se permiten cuentas de ${domains.join(' o ')}.` : '';
      return `Esa cuenta no pertenece a la empresa.${suffix}`;
    }
    case 'account-disabled':
      return 'Tu cuenta está desactivada. Contacta a RR.HH.';
    case 'bad-credentials':
      // Deliberately does not say which of the two was wrong.
      return 'Correo o contraseña incorrectos.';
    case 'network':
      return 'No hay conexión. Revisa tu red e intenta de nuevo.';
    /*
     * Accionable y sin inventar la causa. El caso real que lo motivó era la
     * función de acceso tardando más de lo que Google le permite, y un segundo
     * intento funcionaba; pero `unknown` agrupa cualquier otro código, así que el
     * mensaje dice qué hacer y no por qué pasó.
     */
    case 'unknown':
      return 'No pudimos completar el acceso. Vuelve a pulsar «Continuar con Google»; si falla otra vez, espera un momento y reintenta.';
  }
};
